import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminProjects = () => {
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(searchParams.get('action') === 'new');
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', location: '', category: 'Residential' });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('admin_token');

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const addFiles = (files) => {
    const newFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const removeNewFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', location: '', category: 'Residential' });
    setSelectedFiles([]);
    setExistingImages([]);
    setEditingProject(null);
    setShowForm(false);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || '',
      description: project.description || '',
      location: project.location || '',
      category: project.category || 'Residential',
    });
    setExistingImages(project.images || []);
    setSelectedFiles([]);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('location', formData.location);
    data.append('category', formData.category);

    // Pass existing images to keep
    if (editingProject) {
      data.append('keep_images', JSON.stringify(existingImages));
    }

    // Add new files
    if (selectedFiles.length > 0) {
      selectedFiles.forEach(file => data.append('images', file));
    } else if (!editingProject && existingImages.length === 0) {
      alert('Please add at least one image');
      setSubmitting(false);
      return;
    }

    try {
      if (editingProject) {
        await axios.put(`${API}/api/admin/projects/${editingProject.id}`, data, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/api/admin/projects`, data, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      resetForm();
      fetchProjects();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API}/api/admin/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProjects();
    } catch (err) {
      alert('Failed to delete project');
    }
  };

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  const totalImages = existingImages.length + selectedFiles.length;

  return (
    <div className="p-8" data-testid="admin-projects-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white">Projects</h1>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="add-project-btn"
          >
            <Plus size={16} className="mr-2" /> Add Project
          </Button>
        )}
      </div>

      {/* Project Form */}
      {showForm && (
        <Card className="bg-gray-900 border-gray-800 mb-8" data-testid="project-form">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {editingProject ? 'Edit Project' : 'New Project'}
              </h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="e.g. Kitchen Renovation - Ipswich"
                    data-testid="project-title-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="e.g. Ipswich, Suffolk"
                    data-testid="project-location-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-3 min-h-[100px] resize-y"
                  placeholder="Describe the project..."
                  data-testid="project-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Category</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-2"
                  data-testid="project-category-select"
                >
                  <option value="Residential">Residential</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Renovation">Renovation</option>
                  <option value="Extension">Extension</option>
                  <option value="New Build">New Build</option>
                </select>
              </div>

              {/* Drag & Drop Image Upload */}
              <div className="space-y-2">
                <Label className="text-gray-300">
                  Images {!editingProject && '*'} 
                  <span className="text-gray-500 text-xs ml-2">({totalImages} image{totalImages !== 1 ? 's' : ''} total)</span>
                </Label>
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                    dragActive 
                      ? 'border-orange-500 bg-orange-500/10' 
                      : 'border-gray-700 hover:border-orange-600 hover:bg-gray-800/50'
                  }`}
                  data-testid="drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="project-images-input"
                  />
                  <Upload className={`mx-auto mb-3 ${dragActive ? 'text-orange-500' : 'text-gray-500'}`} size={36} />
                  <p className="text-gray-300 font-medium">
                    {dragActive ? 'Drop images here' : 'Drag & drop images here'}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    or click to browse — select as many as you want
                  </p>
                  <p className="text-gray-600 text-xs mt-2">
                    Supports JPEG, PNG, WebP
                  </p>
                </div>

                {/* Existing images (when editing) */}
                {existingImages.length > 0 && (
                  <div className="mt-4">
                    <p className="text-gray-400 text-sm mb-2">Current images ({existingImages.length}):</p>
                    <div className="grid grid-cols-5 md:grid-cols-8 gap-2">
                      {existingImages.map((url, idx) => (
                        <div key={url} className="relative aspect-square rounded overflow-hidden group">
                          <img src={url} alt={`Existing ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(idx)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove"
                          >
                            <X size={12} className="text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newly selected files */}
                {selectedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className="text-gray-400 text-sm mb-2">New images to add ({selectedFiles.length}):</p>
                    <div className="grid grid-cols-5 md:grid-cols-8 gap-2">
                      {selectedFiles.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} className="relative aspect-square rounded overflow-hidden group">
                          <img src={URL.createObjectURL(file)} alt={`New ${idx + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeNewFile(idx)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove"
                          >
                            <X size={12} className="text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700"
                  disabled={submitting}
                  data-testid="project-submit-btn"
                >
                  {submitting ? 'Saving...' : (editingProject ? 'Update Project' : 'Create Project')}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm} className="border-gray-700 text-gray-300">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Projects List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className="bg-gray-900 border-gray-800 overflow-hidden" data-testid={`project-card-${project.id}`}>
            <div className="aspect-video relative bg-gray-800">
              {project.image ? (
                <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <ImageIcon className="text-gray-600" size={40} />
                </div>
              )}
              <div className="absolute top-2 right-2 flex space-x-1">
                <button
                  onClick={() => handleEdit(project)}
                  className="p-2 bg-gray-900/80 rounded-lg text-gray-300 hover:text-orange-400 transition-colors"
                  data-testid={`edit-project-${project.id}`}
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="p-2 bg-gray-900/80 rounded-lg text-gray-300 hover:text-red-400 transition-colors"
                  data-testid={`delete-project-${project.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <CardContent className="p-4">
              <h3 className="text-white font-semibold text-sm truncate">{project.title}</h3>
              <p className="text-gray-400 text-xs mt-1">{project.location || project.category || ''}</p>
              <p className="text-gray-500 text-xs mt-1">{(project.images || []).length} images</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminProjects;
